import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import axiosInstance from '../utils/axiosConfig';
import useTranslation from '../utils/useTranslation';
import { formatDate } from './ArticleModal';
import Header from './Header';
import Footer from './Footer';

// Turn a YouTube watch/short/embed URL into its embeddable form.
function toYouTubeEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    const host = u.hostname.replace(/^www\./, '');
    let id = '';
    if (host === 'youtu.be') {
      id = u.pathname.slice(1);
    } else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (u.pathname.startsWith('/embed/')) id = u.pathname.split('/embed/')[1];
      else if (u.pathname.startsWith('/shorts/')) id = u.pathname.split('/shorts/')[1];
      else id = u.searchParams.get('v') || '';
    }
    id = (id || '').split(/[/?&#]/)[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  } catch {
    return null;
  }
}

function buildVideoEmbed(doc, embedUrl) {
  // Mirror the admin editor's YouTube embed (iframe.ql-video) so it renders
  // identically to the authored content.
  const iframe = doc.createElement('iframe');
  iframe.className = 'ql-video';
  iframe.src = embedUrl;
  iframe.setAttribute('title', 'YouTube video');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute(
    'allow',
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
  );
  iframe.setAttribute('allowfullscreen', '');
  return iframe;
}

// Enhance trusted HTML: embed YouTube links and wrap them responsively.
function enhanceArticleHtml(html) {
  if (!html || typeof window === 'undefined' || !window.DOMParser) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('a[href]').forEach((a) => {
    const embed = toYouTubeEmbed(a.getAttribute('href'));
    if (embed) a.replaceWith(buildVideoEmbed(doc, embed));
  });

  doc.querySelectorAll('video').forEach((v) => {
    const src = v.getAttribute('src') || v.querySelector('source')?.getAttribute('src');
    const embed = toYouTubeEmbed(src);
    if (embed) v.replaceWith(buildVideoEmbed(doc, embed));
  });

  const ytRegex =
    /(https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?\S*v=|embed\/|shorts\/)|youtu\.be\/)\S+)/g;
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    if (node.parentElement?.closest('a, .article-video')) return;
    const text = node.nodeValue;
    ytRegex.lastIndex = 0;
    if (!ytRegex.test(text)) return;
    const frag = doc.createDocumentFragment();
    let lastIndex = 0;
    let m;
    ytRegex.lastIndex = 0;
    while ((m = ytRegex.exec(text)) !== null) {
      if (m.index > lastIndex) frag.appendChild(doc.createTextNode(text.slice(lastIndex, m.index)));
      const embed = toYouTubeEmbed(m[0]);
      frag.appendChild(embed ? buildVideoEmbed(doc, embed) : doc.createTextNode(m[0]));
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) frag.appendChild(doc.createTextNode(text.slice(lastIndex)));
    node.replaceWith(frag);
  });

  return doc.body.innerHTML;
}

// Shared detail page for News and Projects (same API shape).
export default function ArticlePage({ kind = 'news' }) {
  const { t, lang, dir } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const enhancedContent = useMemo(() => enhanceArticleHtml(item?.content), [item?.content]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get(`public/${kind}/${id}/`, { params: { lang } })
      .then((res) => {
        if (!cancelled) setItem(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setItem(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, id, lang]);

  const handleNavigate = (sectionId) => {
    if (sectionId === 'home') {
      navigate('/');
      return;
    }
    if (sectionId === 'about' || sectionId === 'laws') return;
    navigate('/', { state: { scrollTo: sectionId } });
  };

  return (
    <div className="article-page">
      <Header active={kind} onNavigate={handleNavigate} />

      <main className="container article-page-wrap">
        {loading ? (
          <div className="article-page-loading">
            <div className="spinner" />
          </div>
        ) : !item ? (
          <div className="article-page-empty">
            <p>{t('article_not_found')}</p>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
              {t('back')}
            </button>
          </div>
        ) : (
          <article className={`article-detail article-detail--${kind}`}>
            <header className="article-hero-head">
              <div className="article-hero-head-inner">
                {item.uploaded_date && (
                  <time className="article-detail-date" dateTime={item.uploaded_date}>
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {formatDate(item.uploaded_date, lang)}
                  </time>
                )}
                <h1 className="article-detail-title">{item.title}</h1>
              </div>
            </header>

            {item.content && (
              <div className="article-detail-body">
                <div
                  className="article-detail-content"
                  dir={dir}
                  // Content is authored by trusted staff in the control panel.
                  dangerouslySetInnerHTML={{ __html: enhancedContent }}
                />
              </div>
            )}

          </article>
        )}
      </main>

      <Footer />
    </div>
  );
}
